
import { useState, useEffect } from 'react';
import { getMockLeaderboard } from '@/utils/gamification';
import { User } from '@/types/user';
import { LeaderboardEntry, zones } from './LeaderboardTypes';

interface UserProgressType {
  points: number;
  streak: number;
}

export const useLeaderboardData = (user: User | null, userProgress: UserProgressType) => {
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardEntry[]>([]);
  const [selectedZone, setSelectedZone] = useState<string | null>(null);
  
  // When user/progress changes, update the leaderboard
  useEffect(() => {
    if (user) {
      // Get mock leaderboard data
      const mockData = getMockLeaderboard();
      
      // Add random zone activity data to each entry
      const enhancedData = mockData.map(entry => {
        const zoneData: { [key: string]: number } = {};
        zones.forEach(zone => {
          zoneData[zone.id] = Math.floor(Math.random() * 100) + 10;
        });
        
        return {
          ...entry,
          zones: zoneData
        };
      });
      
      // Create current user entry with real progress
      const userEntry: LeaderboardEntry = {
        id: user.id,
        name: user.name,
        points: userProgress.points,
        streak: userProgress.streak,
        avatar: user.avatar,
        zones: enhancedData.find(entry => entry.id === user.id)?.zones || {}
      };
      
      // Replace the mock user entry with real user data
      const updatedLeaderboard = enhancedData.map(entry => 
        entry.id === user.id ? userEntry : entry
      );
      
      // Sort by points
      updatedLeaderboard.sort((a, b) => b.points - a.points);
      
      setLeaderboardData(updatedLeaderboard);
    } else {
      setLeaderboardData(getMockLeaderboard());
    }
  }, [user, userProgress]);

  // Get sorted leaderboard data based on selected zone
  const getSortedData = () => {
    if (!selectedZone) {
      return leaderboardData.sort((a, b) => b.points - a.points);
    }
    
    return [...leaderboardData]
      .sort((a, b) => {
        const aPoints = a.zones?.[selectedZone] || 0;
        const bPoints = b.zones?.[selectedZone] || 0;
        return bPoints - aPoints;
      });
  };
  
  // Get user rank based on selected zone
  const getUserRank = () => {
    if (!user) return 0;
    const sortedData = getSortedData();
    return sortedData.findIndex(item => item.id === user.id) + 1;
  };

  return {
    leaderboardData,
    selectedZone,
    setSelectedZone,
    getSortedData,
    getUserRank
  };
};
